import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { STRINGS } from '@/strings/strings';
import { ApiError } from '@/lib/api-client';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import {
  discardPendingPhotoSwap,
  listBookAnchorAttempts,
  redrawFigures,
  selectBookAnchorAttempt,
  stagePhotoSwap,
} from '../../api';
import type {
  AttemptListResponse,
  EnvelopeImageRefusalCastPhoto,
  PendingPhotoSwapEntry,
  PreviewEnvelope,
} from '../../types';
import { useAnchorAccept } from '../../use-anchor-accept';
import { useAnchorRegen } from '../../use-anchor-regen';
import { usePhotoUpload } from '../../use-photo-upload';
import { Eyebrow } from '../Eyebrow';
import { HintBox } from '../preview/HintBox';
import { StageView } from '../preview/StageView';
import { FotoUploadSlot } from '../personen/FotoUploadSlot';
import type { FotoUploadSlotState } from '../personen/FotoUploadSlot';
import { PhotoSwapDialog } from '../image-refused/ImageRefusedScreen';
import { AnchorNotInReviewOverlay } from './AnchorNotInReviewOverlay';
import { AnchorStaleLine } from './AnchorStaleLine';

/**
 * Screen 2 · Anker-Gate orchestrator (Bau-Spez v3.1 §6 lines 278-303).
 *
 * Renders states 2/3/4 driven by `envelope.cover.remainingAnchor`
 * (Sebastian ruling 2026-08-05 §1 + envelope shape after 96502dd):
 *
 *   remainingAnchor === 2 → state 2 (erster Anblick, no hint)
 *   remainingAnchor === 1 → state 3 (regen hint under outline)
 *   remainingAnchor === 0 → state 4 (outline gone, HintBox + Zurück outline)
 *
 * States 6/7 are transient error overlays triggered by the accept /
 * regen mutations:
 *
 *   error_code === 'anchor_attempt_stale'   → state 6 inline
 *                                             (AnchorStaleLine +
 *                                              40 % opacity)
 *   error_code === 'anchor_not_in_review'   → state 7 full overlay
 *                                             (AnchorNotInReviewOverlay)
 *
 * Both trigger `onRefetch()`; BookRoute re-dispatches when the fresh
 * envelope arrives with the new status. State 5 (regen läuft) is
 * handled at BookRoute level via `anchor_pending && remainingAnchor < 2`
 * — not here.
 *
 * ═══ ANCHOR PICKER SPLIT (Sebastian ruling 2026-09-08 evening) ═══════
 *
 * Yesterday's picker (f8e4b47) shipped as a plain-text-link menu
 * entry that opened an INLINE picker view where a tile click was
 * `pick IS accept` (status flipped, story enqueued). Sebastian ruled
 * that shape as "right about the backend mechanics and wrong about
 * the screen. Separate them: picking sets the selection and re-
 * renders the screen. Accepting is still its own button." The
 * evolution:
 *
 *   - Entry point: THIRD OUTLINE BUTTON alongside Accept + Regen
 *     (same visual weight), NOT a text link. Gated on 2+ attempts.
 *   - Overlay: Radix Dialog with backdrop, mirrors the cover picker
 *     on the Freigabe screen (`AttemptPickerDialog`), NOT an inline
 *     screen replacement.
 *   - Tile click: writes `Anchor.selectedAnchorAttemptId` via
 *     `selectBookAnchorAttempt`, closes the Dialog, refetches the
 *     envelope. The screen behind now shows the newly-selected
 *     image (envelope derives `cover.imageUrl` from
 *     `selectedAnchorAttemptId ?? newest`). Accept button remains
 *     visible + wired.
 *
 * ═══ FOTO-PFAD ONLY ═════════════════════════════════════════════════
 *
 * Sebastian ruling 2026-08-05 §1: Foto-Pfad text unconditionally
 * until the DTO opens the Text-Pfad and the envelope grows a path
 * signal. Tracker `[book-create-order-dto-blocks-text-path]` records
 * the DTO gap.
 *
 * ═══ "ZURÜCK ZU DEN PERSONEN" DESTINATION ═════════════════════════
 *
 * The text link (states 2/3) and the outline button (state 4) both
 * point at Schritt 1 (Personen). That screen ships in 5a; until then
 * the `/book` placeholder route renders. Once 5a lands, the router
 * dispatches based on `draft_configuring` status naturally — same
 * G12 discipline as state 7 auto-nav.
 */

const BUTTON_GOLD =
  'h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60';

// v3.1 §5 line 210 verbatim outline button class.
const BUTTON_OUTLINE =
  'h-12 w-full rounded-lg border-[1.5px] border-dream-navy bg-dream-white text-base font-semibold text-dream-navy disabled:opacity-50';

const TEXT_LINK = 'text-sm text-dream-navy underline';

export interface AnchorReviewScreenProps {
  readonly envelope: PreviewEnvelope;
  /** Re-fetch the envelope so BookRoute can re-dispatch on status change. */
  readonly onRefetch: () => void;
}

export function AnchorReviewScreen({ envelope, onRefetch }: AnchorReviewScreenProps) {
  const [staleTransient, setStaleTransient] = useState(false);
  const [notInReviewTransient, setNotInReviewTransient] = useState(false);

  // Sebastian ruling 2026-09-11 SLICE D + 2026-09-24 (batch photo-
  // swap slice) — voluntary photo swap flow.
  //
  // ═══ BATCH FLOW (2026-09-24 round 2) ══════════════════════════════
  //
  // Every "Foto ändern" tap STAGES the swap (backend writes to
  // `intake.derived.pendingPhotoSwaps`, no render fires, no cap slot
  // burned). The batch UI lives ENTIRELY INSIDE the picker dialog —
  // page-level pending state does not exist:
  //
  //   - Closing the dialog (Abbrechen / X / Escape / outside click)
  //     DISCARDS every staged swap and returns the page to its
  //     normal shape ("Passt so" + regen button + Foto ändern
  //     visible). Closing == "not now, forget the staging."
  //   - The only way to APPLY staged photos is the gold "Jetzt neues
  //     Figurenbild erstellen" button inside the dialog. On success
  //     the backend transitions anchor_review → anchor_pending;
  //     BookRoute re-dispatches away from this screen.
  //   - Counter "Noch {n} Mal möglich" sits INSIDE the dialog,
  //     below the gold button.
  //   - Redraw error stays inside the dialog too — the customer can
  //     retry from the still-open dialog.
  //
  // On mount, if the envelope already carries pending swaps (e.g.
  // customer reloaded mid-staging), the picker dialog auto-opens so
  // the customer decides again — the model "closing == discard-all"
  // means we would otherwise leave staged swaps stranded on the
  // server.
  //
  // Remaining count is displayed ONCE, on the counter line below the
  // gold button — Sebastian ruling 2026-09-24 point 6 (was "(N)"
  // suffix on the outer button; removed).
  const VOLUNTARY_PHOTO_SWAP_CAP_DEFAULT = 2;
  const voluntaryPhotoSwapsRemaining =
    envelope.voluntaryPhotoSwapsRemaining ?? VOLUNTARY_PHOTO_SWAP_CAP_DEFAULT;
  const voluntarySwapCastPhotos: readonly EnvelopeImageRefusalCastPhoto[] =
    envelope.voluntarySwapCastPhotos ?? [];
  const pendingPhotoSwaps: readonly PendingPhotoSwapEntry[] =
    envelope.pendingPhotoSwaps ?? [];
  const hasPending = pendingPhotoSwaps.length > 0;
  const showVoluntarySwap =
    voluntaryPhotoSwapsRemaining > 0 && voluntarySwapCastPhotos.length > 0;
  // Set of `${castKey}:${whichPhoto}` for O(1) pending lookup inside
  // the picker dialog's row rendering.
  const pendingSet = useMemo(
    () => new Set(pendingPhotoSwaps.map((p) => `${p.castKey}:${p.whichPhoto}`)),
    [pendingPhotoSwaps],
  );
  // Sebastian ruling 2026-09-24 follow-up fix #1 — signed URL for each
  // staged face photo. Picker uses this as the row thumbnail instead
  // of the pre-swap face photo so the customer sees the NEW photo he
  // just picked. `null` when the backend could not sign; the PWA
  // falls back to the pre-swap thumbnail (same photo the customer
  // saw before the swap — worst-case is the customer sees the old
  // thumbnail again but the pending badge tells him the swap
  // succeeded regardless).
  const pendingFacePhotoUrlByCastKey = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const p of pendingPhotoSwaps) {
      if (p.whichPhoto === 'face') m.set(p.castKey, p.newPhotoUrl);
    }
    return m;
  }, [pendingPhotoSwaps]);
  const [swapConfirmDialogOpen, setSwapConfirmDialogOpen] = useState(false);
  const [swapPickerDialogOpen, setSwapPickerDialogOpen] = useState(false);
  const [swapOpenSlot, setSwapOpenSlot] = useState<{
    castKey: string;
    picked: { file: File; previewUrl: string } | null;
  } | null>(null);
  const [swapPhase, setSwapPhase] = useState<'idle' | 'uploading' | 'error'>('idle');
  const swapPhotoUpload = usePhotoUpload();
  // Redraw button state + error line.
  const [redrawPending, setRedrawPending] = useState(false);
  const [redrawErrorVisible, setRedrawErrorVisible] = useState(false);

  // Anchor picker (Sebastian ruling 2026-09-08 evening). The list is
  // fetched silently on mount so the picker button can gate on
  // `attempts.length >= 2` — the customer with only one attempt has
  // nothing to pick between. The list is re-used when the customer
  // opens the picker dialog (no second fetch). `null` = not yet
  // loaded (or errored — treat as no picker). Refetches on envelope
  // change (new attemptId after a successful regen).
  const [pickerAttempts, setPickerAttempts] = useState<AttemptListResponse | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerPickingId, setPickerPickingId] = useState<string | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);

  const accept = useAnchorAccept(envelope.bookOrderId);
  const regen = useAnchorRegen(envelope.bookOrderId);

  const attemptId = envelope.cover.attemptId;
  const remaining = envelope.cover.remainingAnchor;
  const busy = accept.accepting || regen.regenerating;
  // Synchronous in-flight guard against the state-based `busy` race
  // (2026-08-07 fix — the backend log showed four regen POSTs in
  // two seconds). `busy` reflects the LAST rendered state; a rapid
  // second click can dispatch its click event before React commits
  // the `setRegenerating(true)` re-render that disables the button.
  // The ref is checked and set synchronously inside the handler
  // BEFORE the await, so a second call short-circuits regardless of
  // React's render timing. Shared across accept + regen because a
  // customer cannot honestly want both at once — the mutation model
  // is one intent per click. Complementary to the visual
  // `disabled:opacity-60` state disable which stays as-is.
  const inFlightRef = useRef(false);

  const handleErrorCode = useCallback(
    (code: string | null | undefined) => {
      if (code === 'anchor_attempt_stale') {
        setStaleTransient(true);
        onRefetch();
        return;
      }
      if (code === 'anchor_not_in_review') {
        setNotInReviewTransient(true);
        return;
      }
    },
    [onRefetch],
  );

  const handleAccept = useCallback(async () => {
    if (attemptId === null) return;
    // Synchronous ref guard — see the inFlightRef declaration above.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const result = await accept.accept(attemptId);
      if (result.ok) {
        onRefetch();
        return;
      }
      handleErrorCode(result.error?.envelope?.error_code ?? null);
    } finally {
      inFlightRef.current = false;
    }
  }, [accept, attemptId, onRefetch, handleErrorCode]);

  const handleRegen = useCallback(async () => {
    if (attemptId === null) return;
    // Synchronous ref guard — see the inFlightRef declaration above.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const result = await regen.regen(attemptId);
      if (result.ok) {
        onRefetch();
        return;
      }
      handleErrorCode(result.error?.envelope?.error_code ?? null);
    } finally {
      inFlightRef.current = false;
    }
  }, [regen, attemptId, onRefetch, handleErrorCode]);

  // Anchor picker (Sebastian ruling 2026-09-08 evening). Silent fetch
  // on mount + refetch when the current attemptId changes (a fresh
  // regen landed a new attempt). Errors are swallowed — the picker
  // is an opt-in surface; if the list fails to load, the customer
  // simply does not see the button.
  useEffect(() => {
    let cancelled = false;
    void listBookAnchorAttempts(envelope.bookOrderId).then(
      (data) => {
        if (!cancelled) setPickerAttempts(data);
      },
      () => {
        if (!cancelled) setPickerAttempts(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [envelope.bookOrderId, attemptId]);

  // Sebastian ruling 2026-09-24 (batch photo-swap slice, round 2) —
  // "Jetzt neues Figurenbild erstellen" handler. Fires ONE POST
  // /anchor/redraw. On success the backend transitions anchor_review
  // → anchor_pending and enqueues one anchor render; PWA refetches;
  // BookRoute re-dispatches onto the anchor-pending wait screen. On
  // failure the backend rollback restores the pending list so the
  // customer can retry inside the still-open dialog. Returns a
  // success boolean so the caller can close the dialog only on
  // success (leaving the dialog open on failure keeps the error
  // visible + the customer in the batch flow).
  const handleRedraw = useCallback(async (): Promise<boolean> => {
    if (redrawPending) return false;
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    setRedrawPending(true);
    setRedrawErrorVisible(false);
    try {
      await redrawFigures(envelope.bookOrderId);
      onRefetch();
      return true;
    } catch (err) {
      setRedrawErrorVisible(true);
      // eslint-disable-next-line no-console
      console.warn('[anchor-review-batch-redraw] failed', err);
      return false;
    } finally {
      setRedrawPending(false);
      inFlightRef.current = false;
    }
  }, [envelope.bookOrderId, onRefetch, redrawPending]);

  // Sebastian ruling 2026-09-24 (batch photo-swap slice) — discard a
  // staged pending swap ("Altes Foto behalten"). Fires the DELETE
  // endpoint and refetches so the picker row transitions back to the
  // "Foto ändern" affordance.
  const handleDiscardPending = useCallback(
    async (castKey: string, whichPhoto: 'face' | 'fullBody') => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        await discardPendingPhotoSwap(envelope.bookOrderId, castKey, whichPhoto);
        onRefetch();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[anchor-review-batch-discard] failed', err);
      } finally {
        inFlightRef.current = false;
      }
    },
    [envelope.bookOrderId, onRefetch],
  );

  // Sebastian ruling 2026-09-24 (batch photo-swap slice, round 2) —
  // discard-all-and-close. Fires from every dialog exit path
  // (Abbrechen link, X, Escape, outside click) via the picker's
  // `onClose` prop.
  //
  // Close semantics per Sebastian: closing == "not now, forget the
  // staging". Every staged swap is dropped and the page returns to
  // its normal shape. Refetch runs after all discards land so the
  // envelope reflects the empty pending list.
  //
  // Discards run SEQUENTIALLY. `dropPendingSwap`'s conditional
  // updateMany WHERE clause guards on status but not on intake
  // state, so parallel discards could stomp each other's writes.
  // For the max 2 pending entries per book the extra round-trip is
  // ~50 ms per discard — cheap compared to the alternative (added
  // bulk-discard endpoint + backend spec + rollback shape).
  //
  // Preview-URL cleanup + local slot reset happen synchronously so
  // the dialog closes visually right away; discards run in the
  // background and the refetch fires when they land.
  const handleDiscardAllAndClose = useCallback(async () => {
    const snapshot = pendingPhotoSwaps.slice();
    setSwapPickerDialogOpen(false);
    if (swapOpenSlot !== null && swapOpenSlot.picked !== null) {
      URL.revokeObjectURL(swapOpenSlot.picked.previewUrl);
    }
    setSwapOpenSlot(null);
    setSwapPhase('idle');
    setRedrawErrorVisible(false);
    for (const p of snapshot) {
      try {
        await discardPendingPhotoSwap(envelope.bookOrderId, p.castKey, p.whichPhoto);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[anchor-review-close-discard-all] failed', p, err);
      }
    }
    onRefetch();
  }, [pendingPhotoSwaps, envelope.bookOrderId, onRefetch, swapOpenSlot]);

  // Sebastian ruling 2026-09-24 (batch photo-swap slice, round 2) —
  // auto-open the picker dialog on mount when the envelope already
  // carries pending swaps. Covers the reload-mid-staging case:
  // customer staged N photos, reloaded (or the shell remounted).
  // Fires ONCE at mount — the ref prevents re-firing on subsequent
  // envelope refetches (e.g. after a discard, pending goes back to
  // []).
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if ((envelope.pendingPhotoSwaps ?? []).length > 0) {
      autoOpenedRef.current = true;
      setSwapPickerDialogOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePickerOpen = useCallback(() => {
    setPickerError(null);
    setPickerOpen(true);
  }, []);

  const handlePickerClose = useCallback(() => {
    setPickerOpen(false);
    setPickerPickingId(null);
    setPickerError(null);
  }, []);

  const handlePick = useCallback(
    async (pickAttemptId: string) => {
      // Anchor picker SPLIT (Sebastian ruling 2026-09-08 evening) —
      // the pick is now a WRITE-ONLY selection. Backend writes
      // `Anchor.selectedAnchorAttemptId` and returns; no status
      // flip, no story enqueue. On success we CLOSE the dialog and
      // refetch — the anchor screen behind then re-renders with the
      // newly-selected image (envelope derives `cover.imageUrl`
      // from the selection). Accept button remains its own step.
      //
      // Same in-flight ref guard as accept/regen so a rapid double-
      // click cannot fire two writes.
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setPickerPickingId(pickAttemptId);
      setPickerError(null);
      try {
        await selectBookAnchorAttempt(envelope.bookOrderId, pickAttemptId);
        // Close dialog on successful pick (Sebastian ruling: the
        // customer picked, he wants to see his choice on the screen
        // behind, not stay in a grid deciding again).
        setPickerOpen(false);
        onRefetch();
      } catch (err) {
        if (err instanceof ApiError) {
          const code = err.envelope?.error_code ?? null;
          // Reuse the existing state-6/state-7 recovery logic so
          // the picker path lands on the same familiar surfaces as
          // the button-click accept.
          if (code === 'anchor_attempt_stale' || code === 'anchor_not_in_review') {
            handleErrorCode(code);
            setPickerOpen(false);
            return;
          }
          const message =
            typeof err.envelope?.user_message === 'string'
              ? err.envelope.user_message
              : STRINGS.book.review.editorSaveError;
          setPickerError(message);
        } else {
          setPickerError(STRINGS.book.review.editorSaveError);
        }
      } finally {
        setPickerPickingId(null);
        inFlightRef.current = false;
      }
    },
    [envelope.bookOrderId, handleErrorCode, onRefetch],
  );

  if (notInReviewTransient) {
    return <AnchorNotInReviewOverlay onRefetch={onRefetch} />;
  }

  // Anchor picker (Sebastian ruling 2026-09-08 evening) — the picker
  // button is hidden until at least 2 attempts exist: an anchor with
  // only one attempt has nothing to pick between. The list count is
  // authoritative (fetched silently on mount / on attemptId change).
  const pickerAttemptsCount = pickerAttempts?.attempts.length ?? 0;
  const showPickerButton = pickerAttemptsCount >= 2 && !staleTransient;

  // State 6 damping — content underneath at 40 % opacity while the
  // stale line explains + the refetch runs.
  const surfaceOpacity = staleTransient ? 'opacity-40' : '';

  return (
    <div className="space-y-6 py-4">
      {staleTransient && <AnchorStaleLine />}

      <div className={`space-y-6 ${surfaceOpacity}`}>
        {/* Block 4 · 4.2 (Sebastian ruling 2026-09-09) — eyebrow line
            with the gold rule above the image, followed by the book
            title (envelope.title, sourced from titelwunsch fallback
            per Block 3 corrections) in bold as the section heading.
            Only renders the title when non-empty; a book without a
            titelwunsch keeps the pre-Block-4 layout (image at top)
            since there is no book title to show yet. */}
        <div className="flex flex-col gap-3">
          <Eyebrow text={STRINGS.book.anchor.eyebrow} />
          {envelope.title.length > 0 && (
            <h1 className="text-2xl font-semibold text-dream-navy">„{envelope.title}"</h1>
          )}
        </div>

        <StageView
          slot={{
            kind: 'cover',
            imageUrl: envelope.cover.imageUrl,
            imageAlt: STRINGS.book.anchor.imageAlt,
          }}
          canGoPrev={false}
          canGoNext={false}
          onPrev={() => undefined}
          onNext={() => undefined}
        />

        {/* Block 4 · 4.3 · Explanatory text BELOW the image (Sebastian
            ruling 2026-09-09). Singular / plural gated on
            `envelope.figureCount` — the number of figures on the
            anchor image, sourced from `BookOrder.intake.cast.length`.
            Persons and animals count the same (both live in intake
            .cast with a personType discriminator; the anchor render
            draws one figure per entry). Exactly 1 → singular; 2 or
            more (and the defensive 0 fallback for legacy books) →
            plural. */}
        <p className="mx-auto max-w-[520px] text-center text-base leading-relaxed text-dream-navy">
          {envelope.figureCount === 1
            ? STRINGS.book.anchor.frozenTextSingular
            : STRINGS.book.anchor.frozenTextPlural}
        </p>

        <div className="space-y-3">
          {/*
            Sebastian ruling 2026-08-12 (wave commit 3 addition):
            spinner alongside the button label while the accept/regen
            call is in flight. Same class as the empty wait screen the
            morning wave closed — pre-fix "the customer cannot tell
            whether anything is happening" between the click and the
            transition. Disabled state alone is a subtle signal;
            explicit motion is unambiguous.
          */}
          {/*
            Sebastian ruling 2026-09-24 (batch photo-swap slice, round
            2) — the "Passt so" button is UNCONDITIONALLY visible on
            the page. Under the round-2 model the pending state lives
            entirely inside the dialog; closing the dialog discards
            every staged swap and returns the page to its normal shape.
            The page therefore never needs to gate the accept button on
            pending — pending swaps only exist while the dialog is
            open (which aria-hides the page), or as the reload-mid-
            staging window before the auto-open effect fires.
          */}
          <button
            type="button"
            disabled={busy || staleTransient || attemptId === null}
            onClick={handleAccept}
            aria-busy={accept.accepting}
            className={`${BUTTON_GOLD} inline-flex items-center justify-center gap-2`}
          >
            <span>{STRINGS.book.anchor.buttonAccept}</span>
            {accept.accepting && <ButtonSpinner />}
          </button>

          {remaining > 0 ? (
            <>
              {/*
                Sebastian ruling 2026-09-24 round 2 — the regen button
                (renamed "Figurenbild neu erstellen") is
                unconditionally visible when remaining > 0. Under the
                round-2 model pending swaps live inside the dialog
                only; the regen button and the batch flow are never
                both visible to the customer at the same time.
              */}
              <button
                type="button"
                disabled={busy || staleTransient || attemptId === null}
                onClick={handleRegen}
                aria-busy={regen.regenerating}
                className={`${BUTTON_OUTLINE} inline-flex items-center justify-center gap-2`}
              >
                <span>{STRINGS.book.anchor.buttonRegen}</span>
                {regen.regenerating && <ButtonSpinner />}
              </button>
              {remaining === 1 && (
                <p className="mx-auto max-w-[520px] text-center text-xs text-dream-navy/70">
                  {STRINGS.book.anchor.regenHintAfterFirst}
                </p>
              )}
            </>
          ) : (
            <>
              <HintBox>
                <p className="text-sm text-dream-navy leading-relaxed">
                  {showVoluntarySwap
                    ? STRINGS.book.anchor.regenExhaustedBodyWithSwap
                    : STRINGS.book.anchor.regenExhaustedBodyNoSwap}
                </p>
              </HintBox>
              {/* Block 4 · 4.4 (Sebastian ruling 2026-09-09) — the
                  "Zurück zu den Personen" outline button was here.
                  Removed entirely. The customer stays on the Figuren
                  screen with the HintBox line pointing at how to
                  proceed (change Personen descriptions and try
                  again). See the 2.11 REPORT on the previous
                  commit's investigation: the back-link either
                  bounces to the same screen (when
                  user.pendingBookOrderId is set) or lands on an
                  empty configurator (store was reset on order
                  creation) — neither is useful. Removal is the fix.
                  Block 4.5 will replace the surrounding UI with a
                  failure-state UI once Sebastian rules on which
                  surface it covers. */}
            </>
          )}

          {/*
            Anchor picker SPLIT (Sebastian ruling 2026-09-08 evening) —
            THIRD BUTTON alongside Accept + Regen, same visual weight
            (outline). Gated on 2+ attempts. Yesterday's plain-text-
            link entry point was deleted with this build; the button
            promotes the picker to a peer of the other two actions.
          */}
          {showPickerButton && (
            <button
              type="button"
              disabled={busy || staleTransient}
              onClick={handlePickerOpen}
              className={BUTTON_OUTLINE}
            >
              {STRINGS.book.anchor.anchorPickerMenuEntry}
            </button>
          )}

          {/*
            Sebastian ruling 2026-09-11 SLICE D — "Foto ändern" outline
            button + remaining count. Gated on
            `voluntaryPhotoSwapsRemaining > 0` (cap = 2). Click opens
            the shared photo-swap dialog. Reuses the released button
            label from the ImageRefusedScreen swap flow. The remaining
            count renders in parentheses after the label so the customer
            knows how many changes are still available.
          */}
          {showVoluntarySwap && (
            <button
              type="button"
              disabled={busy || staleTransient}
              onClick={() => setSwapConfirmDialogOpen(true)}
              className={BUTTON_OUTLINE}
            >
              {STRINGS.book.imageRefused.swapButtonLabel}
            </button>
          )}
        </div>

        {/* Block 4 · 4.4 (Sebastian ruling 2026-09-09) — the bottom
            "Zurück zu den Personen" text link (previously rendered
            for remaining > 0) was here. Removed entirely for the
            same reason as the outline button above: the back-link
            navigates to /book which either bounces the customer
            back to the same screen or lands on an empty
            configurator. "Neu zeichnen" stays as the only regen
            affordance. */}

        {/*
          Sebastian ruling 2026-09-24 (batch photo-swap slice, round 2)
          — page-level pending UI is GONE. The redraw button + counter
          + intro line + approval-gate all moved inside the picker
          dialog. Closing the dialog discards every staged swap and
          returns the page to the shape rendered above (Passt so +
          regen + Foto ändern), so the page NEVER carries a pending
          indicator.
        */}
      </div>

      {pickerAttempts !== null && (
        <AnchorPickerDialog
          open={pickerOpen}
          attempts={pickerAttempts}
          pickingId={pickerPickingId}
          errorMessage={pickerError}
          onPick={handlePick}
          onClose={handlePickerClose}
        />
      )}

      {/* Sebastian correction slice 2026-09-10 (Part 2.4) — shared
          photo-swap dialog. Fires BEFORE the picker to enforce the
          "So wird die Figur gut" confirmation. Reuses the exact
          component + copy from ImageRefusedScreen. */}
      <PhotoSwapDialog
        open={swapConfirmDialogOpen}
        onCancel={() => setSwapConfirmDialogOpen(false)}
        onConfirm={() => {
          setSwapConfirmDialogOpen(false);
          setSwapPickerDialogOpen(true);
        }}
      />

      {/* Cast picker dialog. Sebastian ruling 2026-09-24 (batch photo-
          swap slice) — the save now STAGES the swap instead of firing
          it. Each row that ALREADY has a staged photo renders the
          "Neues Foto ausgewählt" badge + "Altes Foto behalten"
          affordance instead of the "Foto ändern" button; customer can
          stage multiple figures in ONE dialog session and close when
          done. */}
      <VoluntarySwapPickerDialog
        open={swapPickerDialogOpen}
        castPhotos={voluntarySwapCastPhotos}
        pendingSet={pendingSet}
        pendingFacePhotoUrlByCastKey={pendingFacePhotoUrlByCastKey}
        hasPending={hasPending}
        redrawPending={redrawPending}
        redrawErrorVisible={redrawErrorVisible}
        voluntaryPhotoSwapsRemaining={voluntaryPhotoSwapsRemaining}
        onRedraw={async () => {
          const ok = await handleRedraw();
          if (ok) setSwapPickerDialogOpen(false);
        }}
        openSlot={swapOpenSlot}
        phase={swapPhase}
        onCastRowClick={(castKey) => {
          if (swapOpenSlot !== null && swapOpenSlot.picked !== null) {
            URL.revokeObjectURL(swapOpenSlot.picked.previewUrl);
          }
          setSwapOpenSlot({ castKey, picked: null });
          setSwapPhase('idle');
        }}
        onFileSelected={(file) => {
          if (swapOpenSlot === null) return;
          if (swapOpenSlot.picked !== null) {
            URL.revokeObjectURL(swapOpenSlot.picked.previewUrl);
          }
          setSwapOpenSlot({
            castKey: swapOpenSlot.castKey,
            picked: { file, previewUrl: URL.createObjectURL(file) },
          });
        }}
        onFileRemove={() => {
          if (swapOpenSlot === null) return;
          if (swapOpenSlot.picked !== null) {
            URL.revokeObjectURL(swapOpenSlot.picked.previewUrl);
          }
          setSwapOpenSlot({ castKey: swapOpenSlot.castKey, picked: null });
          setSwapPhase('idle');
        }}
        onSaveSwap={async () => {
          if (swapOpenSlot === null || swapOpenSlot.picked === null) return;
          setSwapPhase('uploading');
          try {
            const [key] = await swapPhotoUpload.upload([swapOpenSlot.picked.file]);
            if (key === undefined) {
              throw new Error('anchor-review voluntary-swap: upload returned no key');
            }
            // Sebastian ruling 2026-09-24 (batch photo-swap slice) —
            // STAGE the swap (writes to intake.derived.pendingPhotoSwaps,
            // no status flip, no render fires). The dialog stays open
            // so the customer can stage more figures in one session;
            // the row itself flips to the "Neues Foto ausgewählt"
            // shape after refetch below.
            await stagePhotoSwap(envelope.bookOrderId, swapOpenSlot.castKey, {
              whichPhoto: 'face',
              newPhotoKey: key,
            });
            if (swapOpenSlot.picked !== null) {
              URL.revokeObjectURL(swapOpenSlot.picked.previewUrl);
            }
            setSwapOpenSlot(null);
            setSwapPhase('idle');
            onRefetch();
          } catch (err) {
            setSwapPhase('error');
            // eslint-disable-next-line no-console
            console.warn('[anchor-review-voluntary-stage] failed', err);
          }
        }}
        onDiscardPending={(castKey) => {
          // Face is the only slot the picker exposes today; if the
          // fullBody slot ever surfaces here, this callback signature
          // grows a `whichPhoto` param.
          void handleDiscardPending(castKey, 'face');
        }}
        onClose={() => {
          // Sebastian ruling 2026-09-24 (batch photo-swap slice,
          // round 2) — closing the dialog (Abbrechen / X / Escape /
          // outside click) discards every staged swap. The dialog
          // closes immediately for UX; discards fire in the
          // background and the refetch lands once they complete.
          void handleDiscardAllAndClose();
        }}
      />
    </div>
  );
}

/**
 * Sebastian correction slice 2026-09-10 (Part 2.3) — voluntary photo-
 * swap cast picker. Same visual shape as the ImageRefusedScreen swap
 * section (name + face thumbnail + FotoUploadSlot when the row is
 * open), just wrapped in a Radix Dialog for the voluntary-swap flow
 * on the AnchorReviewScreen.
 */
interface VoluntarySwapPickerDialogProps {
  readonly open: boolean;
  readonly castPhotos: readonly EnvelopeImageRefusalCastPhoto[];
  /**
   * Sebastian ruling 2026-09-24 (batch photo-swap slice) — set of
   * `${castKey}:${whichPhoto}` for O(1) pending lookup. Used to
   * render "Neues Foto ausgewählt" + "Altes Foto behalten" on rows
   * that have a staged swap.
   */
  readonly pendingSet: ReadonlySet<string>;
  /**
   * Sebastian ruling 2026-09-24 follow-up fix #1 — signed URL for
   * each staged face photo. Picker uses it as the row thumbnail
   * instead of `entry.facePhotoUrl` when the row has a pending face
   * swap. `null` when the backend could not sign; the picker falls
   * back to the pre-swap thumbnail.
   */
  readonly pendingFacePhotoUrlByCastKey: ReadonlyMap<string, string | null>;
  /**
   * Sebastian ruling 2026-09-24 round 2 — whether ANY voluntary
   * photo swap is pending. When true, the dialog renders the
   * primary "Jetzt neues Figurenbild erstellen" button + counter
   * line below it. Close link is ALWAYS "Abbrechen" (round 2
   * removes the "Schließen" variant).
   */
  readonly hasPending: boolean;
  readonly redrawPending: boolean;
  /** Sebastian ruling 2026-09-24 round 2 — redraw error line lives
   *  INSIDE the dialog now. */
  readonly redrawErrorVisible: boolean;
  /** Sebastian ruling 2026-09-24 round 2 — remaining redraws;
   *  rendered below the gold button as the counter line. */
  readonly voluntaryPhotoSwapsRemaining: number;
  readonly onRedraw: () => void;
  readonly openSlot: {
    castKey: string;
    picked: { file: File; previewUrl: string } | null;
  } | null;
  readonly phase: 'idle' | 'uploading' | 'error';
  readonly onCastRowClick: (castKey: string) => void;
  readonly onFileSelected: (file: File) => void;
  readonly onFileRemove: () => void;
  readonly onSaveSwap: () => void;
  /** Sebastian ruling 2026-09-24 (batch photo-swap slice) — discard a
   *  staged pending swap ("Altes Foto behalten"). */
  readonly onDiscardPending: (castKey: string) => void;
  /** Sebastian ruling 2026-09-24 round 2 — fires from all four exit
   *  paths (Abbrechen / X / Escape / outside click) and discards
   *  every staged swap before closing. */
  readonly onClose: () => void;
}

function VoluntarySwapPickerDialog({
  open,
  castPhotos,
  pendingSet,
  pendingFacePhotoUrlByCastKey,
  hasPending,
  redrawPending,
  redrawErrorVisible,
  voluntaryPhotoSwapsRemaining,
  onRedraw,
  openSlot,
  phase,
  onCastRowClick,
  onFileSelected,
  onFileRemove,
  onSaveSwap,
  onDiscardPending,
  onClose,
}: VoluntarySwapPickerDialogProps) {
  const slotState: FotoUploadSlotState =
    openSlot === null || openSlot.picked === null
      ? { kind: 'empty' }
      : { kind: 'picked', previewUrl: openSlot.picked.previewUrl };
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle className="text-lg font-semibold text-dream-navy pr-6">
          {STRINGS.book.imageRefused.swapSectionHeading}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {STRINGS.book.imageRefused.swapSectionHeading}
        </DialogDescription>
        <ul className="space-y-4">
          {castPhotos.map((entry) => {
            const isOpen = openSlot !== null && openSlot.castKey === entry.castKey;
            // Sebastian ruling 2026-09-24 (batch photo-swap slice) —
            // per-row state. Rows with a staged swap show the badge
            // + "Altes Foto behalten" affordance instead of the
            // "Foto ändern" button. The picker only exposes the face
            // slot today; if fullBody is added later, the lookup key
            // grows a second slot check.
            const hasFacePending = pendingSet.has(`${entry.castKey}:face`);
            // Sebastian ruling 2026-09-24 follow-up fix #1 — thumbnail
            // resolution: when the row has a staged face swap, prefer
            // the freshly-signed newPhotoUrl from the envelope so the
            // customer sees the NEW photo he just picked. `null` from
            // the map (signing failed) falls back to the pre-swap
            // face photo; a customer with a null pre-swap thumbnail
            // still gets a placeholder.
            const stagedThumbnailUrl = hasFacePending
              ? pendingFacePhotoUrlByCastKey.get(entry.castKey) ?? null
              : null;
            const thumbnailUrl = stagedThumbnailUrl ?? entry.facePhotoUrl;
            return (
              <li key={entry.castKey} className="space-y-3">
                <div className="flex items-center gap-3">
                  {thumbnailUrl !== null && (
                    <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-dream-navy/10">
                      <img
                        src={thumbnailUrl}
                        alt={STRINGS.book.photoRejected.rejectedTileAria}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-base text-dream-navy">{entry.name}</p>
                    {hasFacePending && (
                      <p className="text-xs font-medium text-dream-navy">
                        {STRINGS.book.batchPhotoSwap.pendingBadge}
                      </p>
                    )}
                  </div>
                  {hasFacePending ? (
                    <button
                      type="button"
                      onClick={() => onDiscardPending(entry.castKey)}
                      className="shrink-0 rounded-lg border border-dream-navy/30 px-3 py-2 text-sm text-dream-navy hover:bg-dream-background"
                    >
                      {STRINGS.book.batchPhotoSwap.discardButton}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onCastRowClick(entry.castKey)}
                      disabled={phase === 'uploading'}
                      className="shrink-0 rounded-lg border border-dream-navy/30 px-3 py-2 text-sm text-dream-navy hover:bg-dream-background disabled:opacity-50"
                    >
                      {STRINGS.book.imageRefused.swapButtonLabel}
                    </button>
                  )}
                </div>
                {isOpen && !hasFacePending && (
                  <div className="space-y-3">
                    <FotoUploadSlot
                      state={slotState}
                      onFileSelected={onFileSelected}
                      onRemove={onFileRemove}
                      showRules={false}
                      ariaLabel={STRINGS.book.photoRejected.slotAriaEmpty}
                      castName={entry.name}
                    />
                    {phase === 'uploading' && (
                      <p className="text-sm text-dream-navy/70">
                        {STRINGS.book.photoRejected.uploadingHeading}
                      </p>
                    )}
                    {phase === 'error' && (
                      <p className="text-sm text-error">
                        {STRINGS.book.photoRejected.swapErrorPlaceholder}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={onSaveSwap}
                      disabled={openSlot?.picked === null || phase === 'uploading'}
                      className={`${BUTTON_GOLD}`}
                    >
                      {phase === 'error'
                        ? STRINGS.book.photoRejected.retryCta
                        : STRINGS.book.personen.blockWeiterLabel}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {/*
          Sebastian ruling 2026-09-24 (batch photo-swap slice, round
          2) — the ONLY way to apply staged photos is this gold
          button. Counter line sits directly below it. Redraw error
          renders inside the dialog too — on failure the backend
          rollback restores the pending list and the dialog stays
          open so the customer can retry.
        */}
        {hasPending && (
          <div className="space-y-2 pt-2">
            <button
              type="button"
              onClick={onRedraw}
              disabled={redrawPending}
              aria-busy={redrawPending}
              className={`${BUTTON_GOLD} inline-flex items-center justify-center gap-2`}
            >
              <span>{STRINGS.book.batchPhotoSwap.redrawButton}</span>
              {redrawPending && <ButtonSpinner />}
            </button>
            <p className="text-center text-xs text-dream-navy/70">
              {STRINGS.book.batchPhotoSwap.counterLine(voluntaryPhotoSwapsRemaining)}
            </p>
            {redrawErrorVisible && (
              <p role="alert" className="text-center text-sm text-error">
                {STRINGS.book.batchPhotoSwap.redrawError}
              </p>
            )}
          </div>
        )}
        {/*
          Sebastian ruling 2026-09-24 (batch photo-swap slice, round
          2) — close link is ALWAYS "Abbrechen". Every exit path
          (this link, X, Escape, outside click) fires onClose which
          discards every staged swap and returns the page to its
          normal shape.
        */}
        <div className="flex justify-center pt-2">
          <button type="button" onClick={onClose} className={TEXT_LINK}>
            {STRINGS.book.review.editorCancelButton}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Anchor picker dialog (Sebastian ruling 2026-09-08 evening) — Radix
 * Dialog wrapping the tile grid, mirrors the cover picker's Dialog
 * shape on the Freigabe screen (`AttemptPickerDialog` at
 * `pwa/src/features/book/components/review/AttemptPickerDialog.tsx`).
 * Full backdrop, escape / click-outside / explicit Close button
 * dismisses.
 *
 * Differences from the cover picker (still deliberate):
 *   - Per-tile `Version N` label below each tile per Sebastian's
 *     released wording. The cover picker has no per-tile label
 *     because Sebastian ruled "whoever is happy should never think
 *     in attempts" for the cover surface. The anchor surface has a
 *     more critical stakes discipline: "a bad anchor is the whole
 *     book", so the customer benefits from the ordinal.
 *   - Tile click writes SELECTION only (no accept semantics). Accept
 *     remains its own button on the anchor screen behind.
 */
interface AnchorPickerDialogProps {
  readonly open: boolean;
  readonly attempts: AttemptListResponse;
  readonly pickingId: string | null;
  readonly errorMessage: string | null;
  readonly onPick: (attemptId: string) => void;
  readonly onClose: () => void;
}

function AnchorPickerDialog({
  open,
  attempts,
  pickingId,
  errorMessage,
  onPick,
  onClose,
}: AnchorPickerDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle className="text-lg font-semibold text-dream-navy pr-6">
          {STRINGS.book.anchor.anchorPickerMenuEntry}
        </DialogTitle>
        {/* Radix Dialog requires an accessible description; the
            picker's visual content is self-explanatory (grid of
            images), so we use an sr-only description that mirrors
            the title for screen-reader users. */}
        <DialogDescription className="sr-only">
          {STRINGS.book.anchor.anchorPickerMenuEntry}
        </DialogDescription>

        {errorMessage !== null && (
          <p role="alert" className="text-sm text-error">
            {errorMessage}
          </p>
        )}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {attempts.attempts.map((a) => {
            const isActive = a.attemptId === attempts.activeAttemptId;
            const isPicking = pickingId === a.attemptId;
            return (
              <div key={a.attemptId} className="space-y-2">
                <button
                  type="button"
                  onClick={() => (isActive || isPicking ? undefined : onPick(a.attemptId))}
                  disabled={isActive || isPicking}
                  aria-pressed={isActive}
                  aria-label={STRINGS.book.anchor.anchorPickerVersionLabel(a.attemptNo)}
                  className={
                    'relative aspect-square w-full overflow-hidden rounded-lg ring-2 transition-shadow ' +
                    (isActive
                      ? 'ring-dream-navy cursor-default'
                      : 'ring-dream-navy/20 hover:ring-dream-navy focus-visible:ring-dream-navy')
                  }
                >
                  <img
                    src={a.imageUrl}
                    alt=""
                    aria-hidden="true"
                    className="h-full w-full object-cover"
                  />
                  {isActive && (
                    <span className="absolute left-2 top-2 rounded-md bg-dream-navy px-2 py-0.5 text-xs font-semibold text-dream-white">
                      {STRINGS.book.anchor.anchorPickerActiveBadge}
                    </span>
                  )}
                  {isPicking && (
                    <span className="absolute inset-0 flex items-center justify-center bg-dream-navy/30">
                      <span
                        aria-hidden
                        className="h-6 w-6 animate-spin rounded-full border-[3px] border-dream-white border-t-transparent"
                      />
                    </span>
                  )}
                </button>
                <p className="text-center text-sm font-medium text-dream-navy">
                  {STRINGS.book.anchor.anchorPickerVersionLabel(a.attemptNo)}
                </p>
              </div>
            );
          })}
        </div>

        <div className="flex justify-center pt-2">
          <button type="button" onClick={onClose} className={TEXT_LINK}>
            {STRINGS.book.review.editorCancelButton}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Inline spinner rendered inside the Accept/Regen buttons while the
 * mutation is in flight (Sebastian ruling 2026-08-12, wave commit 3
 * addition). Small enough to sit alongside the label without
 * changing button height; `animate-spin` is Tailwind's built-in
 * rotation utility. Stroke color = current text color so the
 * spinner inherits the gold-button navy text OR the outline-button
 * navy stroke without a second style branch.
 *
 * aria-hidden — the button itself carries `aria-busy` for the
 * assistive-tech signal; the SVG is decorative.
 */
function ButtonSpinner() {
  return (
    <svg
      aria-hidden
      className="h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
